import React, { useEffect, useState } from "react";
import api from "../lib/api";

/* ─── types ───────────────────────────────────────────────── */
interface Property {
  id: number;
  name: string;
  property_type: string;
  address: string;
  county: string;
  token_value: string;
  unit_label: string;
  is_active: boolean;
  units_count: number;
  created_at: string;
}

interface Unit {
  id: number;
  unit_number: string;
  floor: number;
  is_occupied: boolean;
}

/* ─── helpers ─────────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

const TYPE_META: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  residential: { label: "Residential", color: "text-sky-600", bg: "bg-sky-50", icon: "🏠" },
  commercial:  { label: "Commercial",  color: "text-violet-600", bg: "bg-violet-50", icon: "🏢" },
  institution: { label: "Institution", color: "text-amber-600", bg: "bg-amber-50", icon: "🏫" },
  mixed:       { label: "Mixed Use",   color: "text-emerald-600", bg: "bg-emerald-50", icon: "🏙️" },
};

/* ─── add property modal ──────────────────────────────────── */
function PropertyModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: "", property_type: "residential", address: "", county: "",
    token_value: "1.00", unit_label: "litres",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/properties/", form);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(JSON.stringify(err.response?.data) || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { label: "Property Name", key: "name", type: "text", placeholder: "e.g. Sunrise Apartments" },
    { label: "Address", key: "address", type: "text", placeholder: "e.g. Ngong Road, Nairobi" },
    { label: "County", key: "county", type: "text", placeholder: "e.g. Nairobi" },
    { label: "Token Value (KES per unit)", key: "token_value", type: "number", placeholder: "1.00" },
    { label: `Unit Label`, key: "unit_label", type: "text", placeholder: "litres" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        {/* header */}
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">Add New Property</h2>
              <p className="text-sky-300/70 text-[12px] mt-0.5">Fill in the details to register a property</p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}

          {fields.map(({ label, key, type, placeholder }) => (
            <div key={key}>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">{label}</label>
              <input
                type={type}
                value={(form as any)[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={placeholder}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
                required
              />
            </div>
          ))}

          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-1.5">Property Type</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TYPE_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm({ ...form, property_type: key })}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[13px] font-medium transition-all ${
                    form.property_type === key
                      ? `${meta.bg} ${meta.color} border-current shadow-sm`
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span>{meta.icon}</span>
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60">
              {loading ? "Saving…" : "Save Property"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── units panel ─────────────────────────────────────────── */
function UnitsPanel({ property }: { property: Property }) {
  const [units, setUnits] = useState<Unit[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ unit_number: "", floor: "0" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/properties/${property.id}/units/`).then(({ data }) => setUnits(data.results ?? data));

  useEffect(() => { load(); }, [property.id]);

  const addUnit = async () => {
    setSaving(true);
    await api.post(`/properties/${property.id}/units/`, { unit_number: form.unit_number, floor: parseInt(form.floor) });
    await load();
    setAdding(false);
    setForm({ unit_number: "", floor: "0" });
    setSaving(false);
  };

  const occupied = units.filter((u) => u.is_occupied).length;

  return (
    <div className="border-t border-gray-50 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <p className="text-[12.5px] font-semibold text-gray-700">Units ({units.length})</p>
          {units.length > 0 && (
            <span className="text-[11px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md font-medium">
              {occupied} occupied
            </span>
          )}
        </div>
        <button
          onClick={() => setAdding(!adding)}
          className="text-[12px] text-sky-500 hover:text-sky-600 font-semibold flex items-center gap-1 transition-colors"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
          Add Unit
        </button>
      </div>

      {adding && (
        <div className="flex gap-2 mb-3">
          <input
            value={form.unit_number}
            onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
            placeholder="Unit no."
            className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-300"
          />
          <input
            type="number"
            value={form.floor}
            onChange={(e) => setForm({ ...form, floor: e.target.value })}
            placeholder="Floor"
            className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-300"
          />
          <button
            onClick={addUnit}
            disabled={saving || !form.unit_number}
            className="bg-sky-500 text-white px-3 py-1.5 rounded-lg text-[12px] font-semibold hover:bg-sky-600 disabled:opacity-50 transition-all"
          >
            {saving ? "…" : "Add"}
          </button>
          <button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-600 px-1">
            ✕
          </button>
        </div>
      )}

      {units.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {units.map((u) => (
            <span
              key={u.id}
              className={`text-[11.5px] px-2.5 py-1 rounded-lg font-medium border ${
                u.is_occupied
                  ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              {u.unit_number}
              {u.floor > 0 && <span className="opacity-50 ml-1">F{u.floor}</span>}
            </span>
          ))}
        </div>
      ) : !adding ? (
        <p className="text-[12px] text-gray-400">No units yet.</p>
      ) : null}
    </div>
  );
}

/* ─── property card ───────────────────────────────────────── */
function PropertyCard({
  property,
  expanded,
  onToggle,
}: {
  property: Property;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = TYPE_META[property.property_type] ?? TYPE_META.residential;
  const occupancyPct = property.units_count > 0 ? Math.round((Math.floor(property.units_count * 0.7)) / property.units_count * 100) : 0;
  const mockRevenue = Math.round(property.units_count * parseFloat(property.token_value) * 250);

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${expanded ? "border-sky-100" : "border-gray-100"}`}>

      {/* card header stripe */}
      <div className={`h-1.5 ${property.property_type === "residential" ? "bg-gradient-to-r from-sky-400 to-sky-500" : property.property_type === "commercial" ? "bg-gradient-to-r from-violet-400 to-violet-500" : property.property_type === "institution" ? "bg-gradient-to-r from-amber-400 to-amber-500" : "bg-gradient-to-r from-emerald-400 to-emerald-500"}`} />

      <div className="p-5">
        {/* header row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${meta.bg}`}>
              {meta.icon}
            </div>
            <div className="min-w-0">
              <h3 className="text-[14px] font-bold text-gray-800 truncate">{property.name}</h3>
              <p className="text-[12px] text-gray-400 truncate">{property.county} · {property.address.split(",")[0]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${meta.bg} ${meta.color}`}>
              {meta.label}
            </span>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${property.is_active ? "bg-emerald-400" : "bg-gray-300"}`} title={property.is_active ? "Active" : "Inactive"} />
          </div>
        </div>

        {/* stats grid */}
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
            <p className="text-[18px] font-bold text-gray-800">{property.units_count}</p>
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider font-medium mt-0.5">Units</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
            <p className="text-[18px] font-bold text-sky-600">KES {parseFloat(property.token_value).toFixed(0)}</p>
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider font-medium mt-0.5">/{property.unit_label}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-center">
            <p className="text-[18px] font-bold text-emerald-600">{fmt(mockRevenue)}</p>
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider font-medium mt-0.5">KES/mo</p>
          </div>
        </div>

        {/* occupancy bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11.5px] text-gray-500 font-medium">Occupancy</p>
            <p className="text-[11.5px] font-semibold text-gray-700">{occupancyPct}%</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${occupancyPct >= 80 ? "bg-emerald-400" : occupancyPct >= 50 ? "bg-sky-400" : "bg-amber-400"}`}
              style={{ width: `${occupancyPct}%` }}
            />
          </div>
        </div>

        {/* expand toggle */}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between text-[12.5px] font-semibold text-sky-500 hover:text-sky-600 transition-colors py-1"
        >
          <span>{expanded ? "Hide units" : "Manage units"}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}>
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {expanded && <UnitsPanel property={property} />}
      </div>
    </div>
  );
}

/* ─── fmt ─────────────────────────────────────────────────── */
function fmt(n: number) {
  return n.toLocaleString("en-KE");
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function PropertiesPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const load = () => {
    api.get("/properties/")
      .then(({ data }) => setProperties(data.results ?? data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = properties.filter((p) => {
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.county.toLowerCase().includes(search.toLowerCase()) ||
      p.address.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || p.property_type === typeFilter;
    return matchSearch && matchType;
  });

  const totalUnits = properties.reduce((s, p) => s + p.units_count, 0);
  const totalRevenue = properties.reduce((s, p) => s + Math.round(p.units_count * parseFloat(p.token_value) * 250), 0);
  const activeCount = properties.filter((p) => p.is_active).length;

  const TYPE_FILTERS = ["all", "residential", "commercial", "institution", "mixed"];

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Properties</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">
              {properties.length} propert{properties.length === 1 ? "y" : "ies"} under management
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            Add Property
          </button>
        </div>

        {/* ── KPI strip ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: "Total Properties", value: properties.length.toString(), sub: `${activeCount} active`,
                color: "bg-sky-50",
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
              },
              {
                label: "Total Units", value: fmt(totalUnits), sub: `Across all properties`,
                color: "bg-violet-50",
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth={1.8} className="w-5 h-5"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
              },
              {
                label: "Est. Monthly Revenue", value: `KES ${fmt(totalRevenue)}`, sub: `Based on token rates`,
                color: "bg-emerald-50",
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4" strokeLinecap="round"/></svg>,
              },
              {
                label: "Property Types", value: new Set(properties.map((p) => p.property_type)).size.toString(), sub: "Unique categories",
                color: "bg-amber-50",
                icon: <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={1.8} className="w-5 h-5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
              },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>{s.icon}</div>
                <div>
                  <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest">{s.label}</p>
                  <p className="text-[22px] font-bold text-gray-800 leading-tight mt-0.5">{s.value}</p>
                  <p className="text-[11.5px] text-gray-400 mt-0.5">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── filters ── */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* search */}
          <div className="relative flex-1 max-w-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, county, address…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white shadow-sm"
            />
          </div>
          {/* type tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {TYPE_FILTERS.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold capitalize transition-all ${typeFilter === t ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              >
                {t === "all" ? "All" : TYPE_META[t]?.label ?? t}
              </button>
            ))}
          </div>
        </div>

        {/* ── grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-14 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gray-50 flex items-center justify-center text-2xl">🏢</div>
            <p className="font-semibold text-gray-700 text-[14px]">{search || typeFilter !== "all" ? "No properties match your filter" : "No properties yet"}</p>
            <p className="text-[12.5px] text-gray-400 mt-1">
              {search || typeFilter !== "all" ? "Try adjusting your search or filter." : 'Click "Add Property" to register your first one.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <PropertyCard
                key={p.id}
                property={p}
                expanded={expanded === p.id}
                onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
              />
            ))}
          </div>
        )}

      </div>

      {showModal && <PropertyModal onClose={() => setShowModal(false)} onSaved={load} />}
    </>
  );
}
